// Member self-service against the department's Volunteer Management System.
// The member's Supabase access token authenticates them to the VMS — VMS verifies it
// against Supabase's JWKS (ES256, aud=authenticated) — so a member only ever sees/acts
// on their own record. session.js keeps the token refreshed; getAccessToken() returns
// a live one. On 401 the caller should surface a "sign in again" prompt.
import { getAccessToken } from './session';

const VMS_URL = (process.env.REACT_APP_VMS_URL
  || 'https://vms-jgmy2ghs6a-as.a.run.app').replace(/\/$/, '');

// The signed-in member's volunteering summary: phase, approved hours, recognitions,
// applications, and anything self-logged that's still pending. null on any failure;
// { enrolled: false } if they're signed in but not yet a volunteer.
export async function getMyVolunteering() {
  const token = await getAccessToken();
  if (!token) return null;
  try {
    const res = await fetch(`${VMS_URL}/api/member/volunteering`, {
      headers: { authorization: `Bearer ${token}` },
    });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

// Active activities the member can log hours against (public — role titles only).
export async function getSelfLogRoles() {
  try {
    const res = await fetch(`${VMS_URL}/api/public/self-log/roles`);
    if (!res.ok) return [];
    return (await res.json()).items || [];
  } catch {
    return [];
  }
}

// Ulum Ihsan — the member's enrolment (config + their prefs + assigned class).
export async function getUlum(term = 'Term 1') {
  const token = await getAccessToken();
  if (!token) return null;
  try {
    const res = await fetch(`${VMS_URL}/api/member/ulum?term=${encodeURIComponent(term)}`, { headers: { authorization: `Bearer ${token}` } });
    if (!res.ok) return null;
    return await res.json();
  } catch { return null; }
}

// Register for Ulum Ihsan (pick >=2 hubs and >=2 slots).
export async function enrolUlum({ term = 'Term 1', name, hubPrefs, slotPrefs, autoReassign = true }) {
  const token = await getAccessToken();
  if (!token) return { ok: false, error: 'Please sign in first.' };
  try {
    const res = await fetch(`${VMS_URL}/api/member/ulum`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
      body: JSON.stringify({ term, name, hubPrefs, slotPrefs, autoReassign }),
    });
    const d = await res.json().catch(() => ({}));
    return res.ok ? { ok: true, message: d.message } : { ok: false, error: d.error || 'Could not register.' };
  } catch { return { ok: false, error: 'Network error — please try again.' }; }
}

// Log hours against the member's own record — lands pending a coordinator's approval.
export async function selfLogHours({ roleId, sessionDate, hoursLogged, notes }) {
  const token = await getAccessToken();
  if (!token) return { ok: false, error: 'Please sign in first.' };
  try {
    const res = await fetch(`${VMS_URL}/api/member/self-log`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
      body: JSON.stringify({ roleId, sessionDate, hoursLogged, notes }),
    });
    const d = await res.json().catch(() => ({}));
    if (res.ok) return { ok: true, message: d.message };
    return { ok: false, error: d.error || 'Could not log your hours.' };
  } catch {
    return { ok: false, error: 'Network error — please try again.' };
  }
}
